import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { BoltLoader } from '@/components/BoostaBolt';
import api from '@/lib/api';

import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import AcceptInvite from '@/pages/AcceptInvite';
import Dashboard from '@/pages/Dashboard';
import Campaigns from '@/pages/Campaigns';
import CampaignBuilder from '@/pages/CampaignBuilder';
import Creatives from '@/pages/Creatives';
import Composer from '@/pages/Composer';
import Analytics from '@/pages/Analytics';
import BusinessHub from '@/pages/BusinessHub';
import Payments from '@/pages/Payments';
import Settings from '@/pages/Settings';
import Blog from '@/pages/Blog';
import BlogPost from '@/pages/BlogPost';
import Contact from '@/pages/Contact';
import SupportInbox from '@/pages/SupportInbox';
import AppShell from '@/components/layout/AppShell';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-boosta-ink"><BoltLoader size={56} label="Loading Command Centre"/></div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  return <AppShell>{children}</AppShell>;
}

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const ref = React.useRef(false);
  React.useEffect(() => {
    if (ref.current) return;
    ref.current = true;
    const m = window.location.hash.match(/session_id=([^&]+)/);
    if (!m) { navigate('/login', { replace: true }); return; }
    const session_id = decodeURIComponent(m[1]);
    (async () => {
      try {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const { data } = await api.post('/auth/google/callback', { session_id });
        setUser(data);                                   // set auth state directly (avoids reload race)
        window.history.replaceState({}, '', '/dashboard'); // strip the session_id from the URL
        navigate('/dashboard', { replace: true });
        toast.success(`Welcome, ${data?.name?.split(' ')[0] || 'there'}!`);
      } catch (e) {
        toast.error('Google sign-in failed — please try again.');
        navigate('/login', { replace: true });
      }
    })();
  }, [navigate, setUser]);
  return <div className="min-h-screen flex items-center justify-center bg-boosta-ink"><BoltLoader size={48} label="Signing in with Google"/></div>;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/campaigns" element={<ProtectedRoute><Campaigns /></ProtectedRoute>} />
      <Route path="/campaigns/new" element={<ProtectedRoute><CampaignBuilder /></ProtectedRoute>} />
      <Route path="/creatives" element={<ProtectedRoute><Creatives /></ProtectedRoute>} />
      <Route path="/composer" element={<ProtectedRoute><Composer /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
      <Route path="/business-hub" element={<ProtectedRoute><BusinessHub /></ProtectedRoute>} />
      <Route path="/payments" element={<ProtectedRoute><Payments /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/support-inbox" element={<ProtectedRoute><SupportInbox /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster theme="dark" position="top-right" richColors mobileOffset={{ top: 64 }} />
      </AuthProvider>
    </BrowserRouter>
  );
}
